import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { countryToCountryCode } from "lib/data/optionItems";
import { graphql } from "lib/generated/app-service-gql";
import { SignalSettingsQuery } from "lib/generated/app-service-gql/graphql";
import { isDefined } from "lib/utils";
import { DEFAULT_COUNTRY_CODES } from "lib/utils/signalSettingsUtils";
import { useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

const signalSettingsQuery = graphql(`
  query signalSettings($includeNotices: Boolean!, $includeAliases: Boolean!) {
    signalSettings {
      countries
      languages
      keywords
      cpvCodes
      competitors {
        id
        name
        noticeCount @include(if: $includeNotices)
        aliases @include(if: $includeAliases)
        countryCode
      }
      partners {
        id
        name
        noticeCount @include(if: $includeNotices)
        aliases @include(if: $includeAliases)
        countryCode
      }
    }
  }
`);

// The query field is nullable (a team may have no settings yet), but consumers use this
// type for a present settings object — so strip null here and let the hook surface it.
export type SignalSettings = NonNullable<SignalSettingsQuery["signalSettings"]>;

/**
 * The current team's signal-settings criteria: countries, languages, keywords, CPV codes,
 * and competitor/partner organisations. For the individual signal definitions (with ids),
 * use `useSignals`.
 */
export function useSignalSettings(
  { includeNotices, includeAliases }: { includeNotices: boolean; includeAliases: boolean } = {
    includeNotices: false,
    includeAliases: false,
  },
  options?: UseQueryOptions<SignalSettingsQuery, unknown, SignalSettingsQuery, QueryKey>,
) {
  const { data, ...rest } = useGraphQlQuery(
    ["signalSettings", includeNotices, includeAliases],
    signalSettingsQuery,
    [{ includeNotices, includeAliases }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  // Normalise null (team has no settings yet) to undefined so callers keep a single
  // "no settings" case, matching the hook's prior contract.
  return { data: data?.signalSettings ?? undefined, ...rest };
}

/** The team's countries mapped to country codes, falling back to the defaults when unset. */
export function useSignalSettingCountryCodes() {
  const { data: signalSettings } = useSignalSettings();

  return (
    signalSettings?.countries
      ?.map((countryName) => countryToCountryCode(countryName))
      .filter(isDefined) ?? DEFAULT_COUNTRY_CODES
  );
}
